import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Key,
  Home,
  Camera,
  FileText,
  CheckCircle,
  Clock,
  Upload,
  AlertTriangle,
  Zap,
  Calendar,
  DollarSign,
  Shield,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';

const API = getApiBaseUrl();

// Checklist Item Component
const ChecklistItem = ({ item, itemKey, completed, onComplete, disabled }) => {
  const icons = {
    keys_handed: Key,
    meter_readings: Zap,
    inventory_signed: FileText,
    deposit_confirmed: DollarSign,
    tenancy_agreement_signed: FileText,
    standing_order_setup: DollarSign,
    utilities_transferred: Zap,
    emergency_contacts: Shield,
    property_condition_photos: Camera,
    tenant_confirmation: CheckCircle
  };
  
  const labels = {
    keys_handed: "Keys Handed Over",
    meter_readings: "Meter Readings Recorded",
    inventory_signed: "Inventory Signed",
    deposit_confirmed: "Deposit Confirmed",
    tenancy_agreement_signed: "Tenancy Agreement Signed",
    standing_order_setup: "Standing Order Setup",
    utilities_transferred: "Utilities Transferred",
    emergency_contacts: "Emergency Contacts Provided",
    property_condition_photos: "Property Condition Photos",
    tenant_confirmation: "Tenant Confirmation"
  };
  
  const Icon = icons[itemKey] || CheckCircle;
  
  return (
    <div 
      className={`p-4 border rounded-lg flex items-center justify-between ${
        completed ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-full ${
          completed ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
        }`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="font-medium">{labels[itemKey] || itemKey}</div>
          {item.completed_at && (
            <div className="text-xs text-gray-500">
              Completed: {new Date(item.completed_at).toLocaleString()}
            </div>
          )}
        </div>
      </div>
      {!completed && !disabled && (
        <Button size="sm" onClick={() => onComplete(itemKey)}>
          Complete
        </Button>
      )}
      {completed && (
        <Badge className="bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" /> Done
        </Badge>
      )}
    </div>
  );
};

// Meter Reading Input Component
const MeterReadingForm = ({ readings, onSubmit }) => {
  const [electricity, setElectricity] = useState(readings?.electricity || '');
  const [gas, setGas] = useState(readings?.gas || '');
  const [water, setWater] = useState(readings?.water || '');
  
  const handleSubmit = () => {
    onSubmit({ electricity, gas, water });
  };
  
  return (
    <div className="space-y-4">
      <h4 className="font-medium">Meter Readings</h4>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="text-sm text-gray-500">Electricity</label>
          <Input 
            value={electricity} 
            onChange={(e) => setElectricity(e.target.value)}
            placeholder="kWh"
          />
        </div>
        <div>
          <label className="text-sm text-gray-500">Gas</label>
          <Input 
            value={gas} 
            onChange={(e) => setGas(e.target.value)}
            placeholder="m³"
          />
        </div>
        <div>
          <label className="text-sm text-gray-500">Water</label>
          <Input 
            value={water} 
            onChange={(e) => setWater(e.target.value)}
            placeholder="m³"
          />
        </div>
      </div>
      <Button onClick={handleSubmit}>
        <CheckCircle className="h-4 w-4 mr-2" /> Save Readings
      </Button>
    </div>
  );
};

export default function TenancyLifecycle() {
  const { token, user } = useAuth();
  const { tenancyId } = useParams();
  const navigate = useNavigate();
  
  const [tenancy, setTenancy] = useState(null);
  const [moveIn, setMoveIn] = useState(null);
  const [moveOut, setMoveOut] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('move-in');
  
  // Move-out form state
  const [moveOutReason, setMoveOutReason] = useState('tenant_notice');
  const [moveOutDate, setMoveOutDate] = useState('');
  const [moveOutNotes, setMoveOutNotes] = useState('');
  
  const fetchTenancy = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/tenancies/${tenancyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTenancy(data);
      }
    } catch (err) {
      console.error('Tenancy fetch error:', err);
    }
  }, [token, tenancyId]);
  
  const fetchMoveIn = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/tenancy-lifecycle/move-in/${tenancyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMoveIn(data);
      }
    } catch (err) {
      console.error('Move-in fetch error:', err);
    }
  }, [token, tenancyId]);
  
  const fetchMoveOut = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/tenancy-lifecycle/move-out/${tenancyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMoveOut(data);
      }
    } catch (err) {
      // Move-out may not exist yet
    }
  }, [token, tenancyId]);
  
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchTenancy(), fetchMoveIn(), fetchMoveOut()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchTenancy, fetchMoveIn, fetchMoveOut]);
  
  const initiateMoveIn = async () => {
    try {
      const res = await fetch(`${API}/api/tenancy-lifecycle/move-in/${tenancyId}/initiate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Move-in process initiated');
        fetchMoveIn();
      } else {
        toast.error('Failed to initiate move-in');
      }
    } catch (err) {
      toast.error('Error initiating move-in');
    }
  };
  
  const completeChecklistItem = async (item) => {
    try {
      const res = await fetch(`${API}/api/tenancy-lifecycle/move-in/${moveIn.id}/checklist/${item}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: '' })
      });
      if (res.ok) {
        toast.success(`${item} completed`);
        fetchMoveIn();
      }
    } catch (err) {
      toast.error('Error completing item');
    }
  };
  
  const submitMeterReadings = async (readings) => {
    try {
      const res = await fetch(`${API}/api/tenancy-lifecycle/move-in/${moveIn.id}/meter-readings`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ readings })
      });
      if (res.ok) {
        toast.success('Meter readings saved');
        fetchMoveIn();
      }
    } catch (err) {
      toast.error('Error saving readings');
    }
  };
  
  const confirmMoveIn = async () => {
    try {
      const res = await fetch(`${API}/api/tenancy-lifecycle/move-in/${moveIn.id}/confirm`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Move-in confirmed!');
        fetchMoveIn();
      }
    } catch (err) {
      toast.error('Error confirming move-in');
    }
  };
  
  const initiateMoveOut = async () => {
    if (!moveOutDate) {
      toast.error('Please select a move-out date');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/tenancy-lifecycle/move-out/${tenancyId}/initiate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: moveOutReason,
          proposed_end_date: moveOutDate,
          notes: moveOutNotes
        })
      });
      if (res.ok) {
        toast.success('Move-out process initiated');
        fetchMoveOut();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to initiate move-out');
      }
    } catch (err) {
      toast.error('Error initiating move-out');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  const moveOutReasons = [
    { value: 'tenant_notice', label: 'Tenant Notice' },
    { value: 'landlord_notice', label: 'Landlord Notice' },
    { value: 'mutual_agreement', label: 'Mutual Agreement' },
    { value: 'fixed_term_end', label: 'Fixed Term End' },
    { value: 'property_sale', label: 'Property Sale' },
    { value: 'other', label: 'Other' }
  ];
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
          </div>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Home className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Tenancy Lifecycle</h1>
              <p className="text-gray-500">
                {tenancy?.property_address || 'Property Address'}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex gap-4">
            <button
              className={`py-3 px-4 border-b-2 font-medium ${
                activeTab === 'move-in' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('move-in')}
            >
              <ArrowRight className="h-4 w-4 inline mr-2" />
              Move-In
            </button>
            <button
              className={`py-3 px-4 border-b-2 font-medium ${
                activeTab === 'move-out' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('move-out')}
            >
              <ArrowLeft className="h-4 w-4 inline mr-2" />
              Move-Out
            </button>
          </div>
        </div>
      </div>
      
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Move-In Tab */}
        {activeTab === 'move-in' && (
          <div className="space-y-6">
            {!moveIn ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Home className="h-12 w-12 mx-auto mb-4 text-blue-600" />
                  <h3 className="text-xl font-bold mb-2">Start Move-In Process</h3>
                  <p className="text-gray-500 mb-4">
                    Initialize the move-in checklist for this tenancy
                  </p>
                  <Button onClick={initiateMoveIn}>
                    <CheckCircle className="h-4 w-4 mr-2" /> Initiate Move-In
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Status Card */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Move-In Status</CardTitle>
                      <Badge className={
                        moveIn.status === 'completed' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }>
                        {moveIn.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span>Scheduled: {new Date(moveIn.scheduled_date).toLocaleDateString()}</span>
                      </div>
                      {moveIn.completed_at && (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span>Completed: {new Date(moveIn.completed_at).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Checklist */}
                <Card>
                  <CardHeader>
                    <CardTitle>Move-In Checklist</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {moveIn.checklist && Object.entries(moveIn.checklist).map(([key, item]) => (
                      <ChecklistItem
                        key={key}
                        itemKey={key}
                        item={item}
                        completed={item.completed}
                        onComplete={completeChecklistItem}
                        disabled={moveIn.status === 'completed' || (user?.role === 'tenant' && key !== 'tenant_confirmation')}
                      />
                    ))}
                  </CardContent>
                </Card>
                
                {/* Meter Readings */}
                {!moveIn.checklist?.meter_readings?.completed && (
                  <Card>
                    <CardHeader>
                      <CardTitle>
                        <Zap className="h-5 w-5 inline mr-2" />
                        Record Meter Readings
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <MeterReadingForm 
                        readings={moveIn.meter_readings}
                        onSubmit={submitMeterReadings}
                      />
                    </CardContent>
                  </Card>
                )}
                
                {/* Tenant Confirmation */}
                {user?.role === 'tenant' && moveIn.status !== 'completed' && (
                  <Card className="border-green-200 bg-green-50">
                    <CardContent className="p-6 text-center">
                      <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-600" />
                      <h3 className="text-lg font-bold mb-2">Confirm Move-In</h3>
                      <p className="text-gray-600 mb-4">
                        By confirming, you acknowledge that you have received the keys and the property is in acceptable condition.
                      </p>
                      <Button className="bg-green-600 hover:bg-green-700" onClick={confirmMoveIn}>
                        Confirm Move-In Complete
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        )}
        
        {/* Move-Out Tab */}
        {activeTab === 'move-out' && (
          <div className="space-y-6">
            {!moveOut ? (
              <Card>
                <CardHeader>
                  <CardTitle>Initiate Move-Out</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Reason for Ending Tenancy</label>
                    <select
                      className="w-full p-2 border rounded-lg"
                      value={moveOutReason}
                      onChange={(e) => setMoveOutReason(e.target.value)}
                    >
                      {moveOutReasons.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Proposed Move-Out Date</label>
                    <Input
                      type="date"
                      value={moveOutDate}
                      onChange={(e) => setMoveOutDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
                    <Textarea
                      value={moveOutNotes}
                      onChange={(e) => setMoveOutNotes(e.target.value)}
                      placeholder="Any additional information..."
                      rows={3}
                    />
                  </div>
                  
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-yellow-800">Notice Period Required</p>
                        <p className="text-yellow-700">
                          Minimum notice period will be calculated based on your tenancy agreement and the reason for ending.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <Button onClick={initiateMoveOut}>
                    <ArrowLeft className="h-4 w-4 mr-2" /> Initiate Move-Out
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Move-Out Status */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Move-Out Status</CardTitle>
                      <Badge className="bg-orange-100 text-orange-800">
                        {moveOut.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="text-sm text-gray-500">Proposed End Date</div>
                        <div className="font-medium">
                          {new Date(moveOut.proposed_end_date).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="text-sm text-gray-500">Notice Period</div>
                        <div className="font-medium">{moveOut.notice_period_days} days</div>
                      </div>
                    </div>
                    
                    {moveOut.inspection?.scheduled_date && (
                      <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Camera className="h-5 w-5 text-blue-600" />
                          <div>
                            <div className="font-medium text-blue-800">Final Inspection</div>
                            <div className="text-sm text-blue-600">
                              Scheduled: {new Date(moveOut.inspection.scheduled_date).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Checkout Checklist */}
                <Card>
                  <CardHeader>
                    <CardTitle>Checkout Checklist</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {moveOut.checkout_checklist && Object.entries(moveOut.checkout_checklist).map(([key, completed]) => (
                        <div 
                          key={key}
                          className={`p-3 border rounded-lg flex items-center justify-between ${
                            completed ? 'bg-green-50 border-green-200' : 'bg-white'
                          }`}
                        >
                          <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                          {completed ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3 mr-1" /> Done
                            </Badge>
                          ) : (
                            <Badge variant="outline">Pending</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Deposit Status */}
                <Card>
                  <CardHeader>
                    <CardTitle>Deposit Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span>Deposit Release</span>
                        <Badge className={
                          moveOut.deposit_status === 'released' 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }>
                          {moveOut.deposit_status || 'Pending'}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
